/**
 * code-share.ts — CLIENT-SIDE orchestration for G2 E2EE code sharing.
 *
 * This is where the keystore (private keys), the pure crypto (`e2ee`), and the
 * transport (a ciphertext-only side store) meet. It is the ONLY place the plaintext
 * of a shared artifact exists after the caller hands it over: everything that leaves
 * this module toward a transport is ciphertext, wrapped keys, signatures, or public
 * keys. The server / mirror can never decrypt (design: bright-crafting-bee.md, G2).
 *
 * Share flow (author):
 *   provision keystore -> publish signed public keys -> pull the active directory ->
 *   drop any entry whose wrap key is not validly signed by its identity key (F-1) ->
 *   fresh random content key -> encryptArtifact (AAD binds room/ref/version/epoch/alg)
 *   -> wrap the content key for each valid recipient (each wrap Ed25519-signed) ->
 *   sign (room, ref, version, epoch, sha256(ct), contract) with the identity key
 *   (F-7/F-A6) -> hand ciphertext + wraps + sig to the transport.
 *
 * Fetch flow (reader):
 *   load keystore -> pull the row -> pin alg to the client constant (F-3) -> find MY
 *   wrap (a missing wrap is a LOUD "withheld, do not paste plaintext" error, F-A9) ->
 *   VERIFY the author signature over the row's own (version, epoch, contract,
 *   sha256(ct)) so a server cannot relabel any of them (F-7/F-A6) -> unwrap the
 *   content key (verifies the wrap signature under the author identity key, F-1/T-T3)
 *   -> decrypt with the AAD rebuilt from client-trusted ref + author-attested
 *   version/epoch. Any tamper throws with no partial plaintext.
 *
 * `ref` (the file path) is client-trusted context supplied by the caller; the server
 * only ever stores the blind `refIndex`. Everything else the reader trusts is bound
 * into the author signature, never read from the untrusted row unverified.
 */

import { createHash } from "node:crypto";
import { homedir } from "node:os";
import { e2ee, verify } from "@br9704/aethereum-shared";
import {
  provisionKeystore,
  loadKeystore,
  hasKeystore,
  keypairBytes,
  type KeystoreData,
} from "./keystore.js";

const b64 = (bytes: Uint8Array): string => Buffer.from(bytes).toString("base64");
const unb64 = (s: string): Uint8Array => new Uint8Array(Buffer.from(s, "base64"));
const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

/** Client-side max plaintext we will encrypt + share (F-12). The server enforces
 *  its own ceiling too; this is the near-side guard so we never build a huge blob. */
export const MAX_ARTIFACT_BYTES = 1024 * 1024; // 1 MiB

/** Size padding bucket (F-9): the stored `size` is bucketed so the server sees a
 *  size class, not the exact byte length. Content is encrypted regardless; this
 *  only blunts the size-metadata residual. */
const SIZE_BUCKET = 4096;

/** A published directory entry: a member's public keys. Public material only. */
export interface RosterEntry {
  member: string;
  identityPub: string;
  wrapPub: string;
  wrapPubSig: string;
  alg: string;
}

/**
 * The subset of `HiveClient` the orchestration needs. Kept as its own interface so
 * `code-share.ts` depends only on the four ciphertext/public-key transport methods,
 * not the whole client. Both `HttpHiveClient` (local room server) and (in tests) an
 * in-memory transport satisfy it.
 */
export interface CodeShareTransport {
  shareCode(input: {
    refIndex: string;
    version: number;
    keyEpoch: number;
    alg: string;
    ciphertext: string;
    nonce: string;
    wrappedKeys: e2ee.WrappedKeyWire[];
    authorSig: string;
    size: number;
    contract?: string;
    idempotency: string;
  }): Promise<{ id: string }>;
  fetchCode(selector: { id?: string; refIndex?: string }): Promise<{
    id: string;
    refIndex: string;
    version: number;
    keyEpoch: number;
    alg: string;
    ciphertext: string;
    nonce: string;
    wrappedKeys: e2ee.WrappedKeyWire[];
    authorSig: string;
    contract?: string;
    authorMember: string;
  } | null>;
  publishPublicKey(input: {
    identityPub: string;
    wrapPub: string;
    wrapPubSig: string;
    alg: string;
  }): Promise<void>;
  memberPublicKeys(): Promise<RosterEntry[]>;
}

/**
 * v1 blind index for a ref. Stable per (roomId, ref) so both the author and any
 * reader compute the same value for a fetch-by-ref lookup, WITHOUT the plaintext
 * path ever leaving the client.
 *
 * v1 LIMIT (documented, honesty-flagged): this is sha256(roomId || ref), not the
 * HMAC(room-index-key, ref) the design targets. It is stable and room-scoped, but
 * NOT keyed, so a server that already knows a room's id could dictionary-attack
 * common file paths. The artifact CONTENT is fully E2EE regardless; only the
 * path-name blinding is weaker until the keyed room-index key is distributed. This
 * is why the `Verified`/compromise-proof copy stays gated (E2EE_LIVE) until the
 * keyed index lands.
 */
export function refIndexFor(roomId: string, ref: string): string {
  return createHash("sha256").update(roomId).update(" ").update(ref).digest("hex");
}

/** Bucket a byte length up to the next SIZE_BUCKET (never reveals the exact size). */
export function bucketSize(nBytes: number): number {
  return Math.max(SIZE_BUCKET, Math.ceil((nBytes + 1) / SIZE_BUCKET) * SIZE_BUCKET);
}

/**
 * F-1 signed-directory check: a roster entry is usable ONLY if its wrap public key
 * is validly signed by its own identity key, under the pinned algorithm. A server
 * that substitutes an unsigned or wrongly-signed wrap key is dropped here, so the
 * content key is never wrapped to a key the author cannot prove belongs to the
 * claimed identity. (Full active-server resistance additionally requires the OOB
 * safety-number pin from `verification.ts`; this is the always-on baseline.)
 */
export function validRosterEntry(entry: RosterEntry): boolean {
  if (entry.alg !== e2ee.E2EE_ALG) return false;
  try {
    return e2ee.ed25519.verify(unb64(entry.wrapPubSig), unb64(entry.wrapPub), unb64(entry.identityPub));
  } catch {
    return false;
  }
}

/**
 * The public-key-directory subset of the transport. Split out so siblings that
 * share the directory but not the code rail (v4-S10 session handoff) can reuse
 * `provisionAndPublish` without carrying unused code-artifact methods.
 */
export type KeyDirectoryTransport = Pick<CodeShareTransport, "publishPublicKey" | "memberPublicKeys">;

/**
 * Ensure this member's keystore exists (idempotent) and publish its signed public
 * keys to the directory. The wrap key is signed by the identity key so every reader
 * can verify the directory entry (F-1). Returns the keystore.
 */
export async function provisionAndPublish(
  transport: KeyDirectoryTransport,
  roomId: string,
  member: string,
  home: string = homedir(),
): Promise<KeystoreData> {
  const ks = provisionKeystore(roomId, member, home);
  const { identityPriv, identityPub, wrapPub } = keypairBytes(ks);
  const wrapPubSig = e2ee.ed25519.sign(wrapPub, identityPriv);
  await transport.publishPublicKey({
    identityPub: b64(identityPub),
    wrapPub: b64(wrapPub),
    wrapPubSig: b64(wrapPubSig),
    alg: e2ee.E2EE_ALG,
  });
  return ks;
}

export interface ShareCodeResult {
  id: string;
  refIndex: string;
  version: number;
  keyEpoch: number;
  /** Number of members the content key was wrapped for. */
  recipients: number;
  /** Directory entries dropped because their wrap key was not validly signed (F-1). */
  dropped: number;
  /**
   * Members excluded from the wrap because the room requires verified recipients
   * and they are not verified (empty unless `requireVerified` was on).
   */
  excluded: string[];
  /**
   * First-contact (never pinned, never verified) members who WERE wrapped, so
   * callers can warn the human even on the proceed path.
   */
  firstContact: string[];
}

/**
 * Encrypt a code artifact and share it (ciphertext-only) with the room's active,
 * validly-signed members. The plaintext never leaves this call.
 */
export async function shareCode(
  transport: CodeShareTransport,
  opts: {
    roomId: string;
    member: string;
    /** The logical ref (file path). Client-trusted; only its blind index is stored. */
    ref: string;
    plaintext: Uint8Array | string;
    /** Contract this artifact belongs to (server-readable metadata; bound into the sig). */
    contract?: string;
    /** Monotonic version counter. v1 defaults to 1; callers tracking history pass latest+1. */
    version?: number;
    /**
     * Pinned member identities from a prior OOB verification. When present, a
     * recipient whose CURRENT directory identity key diverges from its pin is a HARD
     * block (F-1: the server may be substituting keys). First-contact members (no pin)
     * are allowed through — the room is passive-secure (ciphertext only) but not yet
     * compromise-resistant for them until `aethereum verify` pins + verifies them.
     */
    pins?: Array<{ member: string; identityPub: string }>;
    /**
     * Members covered by an out-of-band safety-number verification (`aethereum
     * verify --confirm`). Used with `pins` to classify each recipient
     * verified / pinned / first-contact.
     */
    verified?: string[];
    /**
     * Room policy (client-enforced; the server is the adversary): when true,
     * only VERIFIED members are wrapped. Non-verified members are excluded and
     * reported in `ShareCodeResult.excluded`; if nobody but the author is
     * verified, the share throws.
     */
    requireVerified?: boolean;
    /**
     * Informed-consent hook for first-contact recipients (F-1). Called with the
     * unpinned members about to receive the wrapped key; return false to abort.
     * Absent => proceed (fail-soft for non-interactive runtimes), with the
     * members reported in `ShareCodeResult.firstContact`.
     */
    onFirstContact?: (
      recipients: Array<{ member: string; identityPub: string }>,
    ) => Promise<boolean> | boolean;
    home?: string;
  },
): Promise<ShareCodeResult> {
  const home = opts.home ?? homedir();
  const plaintext = typeof opts.plaintext === "string" ? utf8(opts.plaintext) : opts.plaintext;
  if (plaintext.length > MAX_ARTIFACT_BYTES) {
    throw new Error(`code artifact too large to share (${plaintext.length} > ${MAX_ARTIFACT_BYTES} bytes)`);
  }

  const ks = await provisionAndPublish(transport, opts.roomId, opts.member, home);
  const { identityPriv } = keypairBytes(ks);

  const directory = await transport.memberPublicKeys();
  const valid = directory.filter(validRosterEntry);
  const dropped = directory.length - valid.length;
  if (valid.length === 0) {
    throw new Error(
      "cannot share code: no validly-signed recipients in the directory (nobody has published a signed wrap key yet)",
    );
  }

  // F-1 active-server defence: if we have pinned any identity keys from a prior OOB
  // verification, a recipient whose CURRENT key diverges from its pin is a HARD block.
  // A first-contact (unpinned) member is not a divergence — passive-secure, but the
  // human must run `aethereum verify` to make it compromise-resistant.
  if (opts.pins && opts.pins.length > 0) {
    const stored = opts.pins.map((p) => ({ member: p.member, identityPub: unb64(p.identityPub) }));
    const presented = valid.map((e) => ({ member: e.member, identityPub: unb64(e.identityPub) }));
    const diverged = verify.detectPinDivergence(stored, presented).filter((d) => d.changed);
    if (diverged.length > 0) {
      throw new Error(
        `refusing to share: the identity key for ${diverged.map((d) => d.member).join(", ")} changed since you verified this room. ` +
          "The server may be substituting keys. Re-run `aethereum verify` and compare the safety number out of band before sharing.",
      );
    }
  }

  // v6-S1 (F-1): classify every recipient client-side. Verified = pinned + covered
  // by an OOB safety-number verification; pinned = TOFU; first-contact = the tier a
  // malicious server would use to inject a rogue member, so it needs consent (or is
  // excluded entirely when the room policy requires verified recipients).
  const pinList = (opts.pins ?? []).map((p) => ({ member: p.member, identityPub: unb64(p.identityPub) }));
  const verifiedSet = new Set(opts.verified ?? []);
  const others = valid.filter((e) => e.member !== opts.member);
  const firstContactEntries = others.filter(
    (e) => verify.classifyRecipient(e.member, pinList, verifiedSet) === "first-contact",
  );
  let wrapTargets = valid;
  let excluded: string[] = [];
  if (opts.requireVerified) {
    excluded = others
      .filter((e) => verify.classifyRecipient(e.member, pinList, verifiedSet) !== "verified")
      .map((e) => e.member);
    wrapTargets = valid.filter((e) => e.member === opts.member || !excluded.includes(e.member));
    if (!wrapTargets.some((e) => e.member !== opts.member)) {
      throw new Error(
        "refusing to share: this room requires verified recipients and none of the other members are verified. " +
          "Run `aethereum verify` with each teammate (compare the safety number out of band, then `aethereum verify --confirm`) and share again.",
      );
    }
  } else if (firstContactEntries.length > 0 && opts.onFirstContact) {
    const proceed = await opts.onFirstContact(
      firstContactEntries.map((e) => ({ member: e.member, identityPub: e.identityPub })),
    );
    if (!proceed) {
      throw new Error(
        "share declined: unverified first-contact recipients. Run `aethereum verify` to pin and verify them, then share again.",
      );
    }
  }
  const firstContact = opts.requireVerified ? [] : firstContactEntries.map((e) => e.member);

  const roomId = opts.roomId;
  const ref = opts.ref;
  const version = opts.version ?? 1;
  const keyEpoch = version; // v1: a fresh content key per artifact; epoch tracks version.
  const contract = opts.contract ?? "";

  const contentKey = e2ee.generateRoomKey();
  const aad = { roomId, ref, version, keyEpoch, alg: e2ee.E2EE_ALG };
  const { ciphertext, nonce } = e2ee.encryptArtifact(contentKey, plaintext, aad);
  const ctHash = e2ee.ciphertextHash(ciphertext);
  const authorSig = e2ee.signArtifact(identityPriv, {
    roomId,
    ref,
    version,
    keyEpoch,
    ciphertextHash: ctHash,
    contract,
  });

  const wrappedKeys: e2ee.WrappedKeyWire[] = wrapTargets.map((entry) => {
    const w = e2ee.wrapRoomKey(contentKey, unb64(entry.wrapPub), entry.member, keyEpoch, identityPriv);
    return {
      member: w.member,
      epk: b64(w.epk),
      nonce: b64(w.nonce),
      ct: b64(w.ct),
      keyEpoch: w.keyEpoch,
      sig: b64(w.sig),
    };
  });

  const refIndex = refIndexFor(roomId, ref);
  const idempotency = Buffer.from(ctHash).toString("hex");
  const { id } = await transport.shareCode({
    refIndex,
    version,
    keyEpoch,
    alg: e2ee.E2EE_ALG,
    ciphertext: b64(ciphertext),
    nonce: b64(nonce),
    wrappedKeys,
    authorSig: b64(authorSig),
    size: bucketSize(plaintext.length),
    contract: contract || undefined,
    idempotency,
  });

  return { id, refIndex, version, keyEpoch, recipients: wrappedKeys.length, dropped, excluded, firstContact };
}

export interface FetchCodeResult {
  plaintext: Uint8Array;
  version: number;
  keyEpoch: number;
  contract?: string;
  authorMember: string;
  /** True once the author signature verified under the author's directory identity key. */
  authorVerified: boolean;
}

/**
 * Fetch, verify, and decrypt a shared code artifact for THIS member. Returns null if
 * no such artifact exists. Throws (never returns plaintext) on any integrity failure:
 * alg downgrade, a missing wrap for this member, an author-signature mismatch, a wrap-
 * signature mismatch, or an AEAD tag failure.
 */
export async function fetchCode(
  transport: CodeShareTransport,
  opts: {
    roomId: string;
    member: string;
    /** The logical ref (file path); required — it is the client-trusted AAD context. */
    ref: string;
    /** Optionally pin a specific artifact id (e.g. a past version); defaults to latest-by-ref. */
    id?: string;
    home?: string;
  },
): Promise<FetchCodeResult | null> {
  const home = opts.home ?? homedir();
  if (!hasKeystore(opts.roomId, home)) {
    throw new Error(`no E2EE keystore for room ${opts.roomId}; provision it (aethereum join / init) before fetching code`);
  }
  const ks = loadKeystore(opts.roomId, home);
  const { wrapPriv } = keypairBytes(ks);

  const roomId = opts.roomId;
  const ref = opts.ref;
  const refIndex = refIndexFor(roomId, ref);
  const selector = opts.id ? { id: opts.id } : { refIndex };
  const row = await transport.fetchCode(selector);
  if (!row) return null;

  // F-3: pin the algorithm to the client constant; refuse any server-offered downgrade.
  if (row.alg !== e2ee.E2EE_ALG) {
    throw new Error(`refusing artifact with unsupported/downgraded alg "${row.alg}" (expected "${e2ee.E2EE_ALG}")`);
  }

  // My wrap is keyed on the directory identity (the caller-supplied member id — the
  // room_members uuid on the hosted path), NOT the keystore's local label, which is
  // only cosmetic. F-A9: a missing wrap is a LOUD signal, never a silent empty. The
  // author may not have shared with you, or a compromised server may be withholding
  // your wrap — either way, do NOT paste plaintext to route around it.
  const mine = row.wrappedKeys.find((w) => w.member === opts.member);
  if (!mine) {
    throw new Error(
      "code withheld: no wrapped key for this member on that artifact. You may not have access, or the server is withholding your key. Do NOT paste plaintext to work around this; ask the author to re-share.",
    );
  }

  // The wrapper AND the author are the same member (the sharer wraps + signs). Pull
  // the author's identity key from the directory to verify both signatures.
  const directory = await transport.memberPublicKeys();
  const author = directory.find((r) => r.member === row.authorMember);
  if (!author) {
    throw new Error("cannot verify artifact: author's public key is not in the directory");
  }
  if (!validRosterEntry(author)) {
    throw new Error("cannot verify artifact: author's directory entry is not validly self-signed (F-1)");
  }
  const authorIdentityPub = unb64(author.identityPub);

  // F-7/F-A6: verify the author signature over the row's OWN (version, epoch, contract,
  // sha256(ct)). If a server relabelled any of them, this fails. Only after this do we
  // trust version/keyEpoch/contract enough to build the AAD from them.
  const ciphertext = unb64(row.ciphertext);
  const ctHash = e2ee.ciphertextHash(ciphertext);
  const authorContract = row.contract ?? "";
  const authorVerified = e2ee.verifyArtifact(
    authorIdentityPub,
    { roomId, ref, version: row.version, keyEpoch: row.keyEpoch, ciphertextHash: ctHash, contract: authorContract },
    unb64(row.authorSig),
  );
  if (!authorVerified) {
    throw new Error(
      "artifact author signature failed to verify (tampered ciphertext, relabelled version/epoch/contract, or wrong author). Refusing to decrypt.",
    );
  }

  // Unwrap the content key (verifies the wrap signature under the author identity key
  // FIRST, then ECDH + AEAD). A tampered wrap or the wrong recipient key throws here.
  const wrapped: e2ee.WrappedKey = {
    member: mine.member,
    epk: unb64(mine.epk),
    nonce: unb64(mine.nonce),
    ct: unb64(mine.ct),
    keyEpoch: mine.keyEpoch,
    sig: unb64(mine.sig),
  };
  const contentKey = e2ee.unwrapRoomKey(wrapped, wrapPriv, authorIdentityPub);

  // Decrypt with the AAD rebuilt from client-trusted ref + author-attested version/epoch.
  const plaintext = e2ee.decryptArtifact(contentKey, ciphertext, unb64(row.nonce), {
    roomId,
    ref,
    version: row.version,
    keyEpoch: row.keyEpoch,
    alg: e2ee.E2EE_ALG,
  });

  return {
    plaintext,
    version: row.version,
    keyEpoch: row.keyEpoch,
    contract: row.contract,
    authorMember: row.authorMember,
    authorVerified: true,
  };
}
